'use client';

import { useCallback } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

export type RecaptchaAction = 'login' | 'register' | 'password_reset';

// Client-side counterpart of the BFF's recaptcha_* errorNames — thrown when a
// site key is configured but the Enterprise script is unavailable (blocked
// extension, offline, still loading). Fail-closed like retail: a missing
// token would be rejected by the configured BFF guard anyway; this surfaces
// the actionable message instead.
export class RecaptchaUnavailableError extends Error {
  errorName = 'recaptcha_unavailable_error';

  constructor() {
    super('reCAPTCHA script is not available');
  }
}

const useRecaptcha = () => {
  const { executeRecaptcha } = useGoogleReCaptcha();

  /**
   * Token for the BFF guard, or `undefined` in degraded (keyless) mode.
   * EWM-314 — consumed by useAccount only; forms never touch tokens.
   */
  const getToken = useCallback(
    async (action: RecaptchaAction): Promise<string | undefined> => {
      if (!process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY) return undefined;
      if (!executeRecaptcha) throw new RecaptchaUnavailableError();
      return executeRecaptcha(action);
    },
    // `executeRecaptcha` flips undefined → defined when the script loads —
    // it MUST be a dependency or consumers capture the unready closure forever.
    [executeRecaptcha],
  );

  return { getToken };
};

export default useRecaptcha;
